import React, { createContext, useState, useCallback, ReactNode } from 'react';
import {
  ticketApi,
  TicketDto,
  CreateTicketRequestDto,
  UpdateTicketRequestDto,
} from '../services/ticketApi';

// ── Context Shape ──────────────────────────────────────────────────────────

interface TicketContextType {
  // State
  tickets: TicketDto[];
  ticket: TicketDto | null;
  loading: boolean;
  error: string | null;

  // Actions
  getAllTickets: () => Promise<TicketDto[]>;
  getUserTickets: () => Promise<TicketDto[]>;
  getTicketById: (_ticketId: number) => Promise<TicketDto | null>;
  createTicket: (_data: CreateTicketRequestDto) => Promise<TicketDto | null>;
  updateTicket: (_ticketId: number, _data: UpdateTicketRequestDto) => Promise<TicketDto | null>;
  deleteTicket: (_ticketId: number) => Promise<boolean>;

  // Utility
  clearError: () => void;
}

export const TicketContext = createContext<TicketContextType | undefined>(undefined);

// ── Provider ───────────────────────────────────────────────────────────────

interface TicketProviderProps {
  children: ReactNode;
}

export const TicketProvider: React.FC<TicketProviderProps> = ({ children }) => {
  const [tickets, setTickets] = useState<TicketDto[]>([]);
  const [ticket, setTicket] = useState<TicketDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const getAllTickets = useCallback(async (): Promise<TicketDto[]> => {
    try {
      setLoading(true);
      setError(null);
      const data = await ticketApi.getAllTickets();
      setTickets(data);
      return data;
    } catch (err: any) {
      const msg = err.message || 'Failed to fetch tickets';
      setError(msg);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getUserTickets = useCallback(async (): Promise<TicketDto[]> => {
    try {
      setLoading(true);
      setError(null);
      const data = await ticketApi.getUserTickets();
      setTickets(data);
      return data;
    } catch (err: any) {
      const msg = err.message || 'Failed to fetch user tickets';
      setError(msg);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getTicketById = useCallback(async (ticketId: number): Promise<TicketDto | null> => {
    try {
      setLoading(true);
      setError(null);
      const data = await ticketApi.getTicketById(ticketId);
      setTicket(data);
      return data;
    } catch (err: any) {
      const msg = err.message || 'Failed to fetch ticket';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const createTicket = useCallback(async (data: CreateTicketRequestDto): Promise<TicketDto | null> => {
    try {
      setLoading(true);
      setError(null);
      const created = await ticketApi.createTicket(data);
      setTickets(prev => [created, ...prev]);
      return created;
    } catch (err: any) {
      const msg = err.message || 'Failed to create ticket';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateTicket = useCallback(async (ticketId: number, data: UpdateTicketRequestDto): Promise<TicketDto | null> => {
    try {
      setLoading(true);
      setError(null);
      const updated = await ticketApi.updateTicket(ticketId, data);
      setTickets(prev => prev.map(t => (t.ticketId === ticketId ? updated : t)));
      if (ticket?.ticketId === ticketId) setTicket(updated);
      return updated;
    } catch (err: any) {
      const msg = err.message || 'Failed to update ticket';
      setError(msg);
      return null;
    } finally {
      setLoading(false);
    }
  }, [ticket]);

  const deleteTicket = useCallback(async (ticketId: number): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);
      await ticketApi.deleteTicket(ticketId);
      setTickets(prev => prev.filter(t => t.ticketId !== ticketId));
      if (ticket?.ticketId === ticketId) setTicket(null);
      return true;
    } catch (err: any) {
      const msg = err.message || 'Failed to delete ticket';
      setError(msg);
      return false;
    } finally {
      setLoading(false);
    }
  }, [ticket]);

  const contextValue: TicketContextType = {
    tickets,
    ticket,
    loading,
    error,
    getAllTickets,
    getUserTickets,
    getTicketById,
    createTicket,
    updateTicket,
    deleteTicket,
    clearError,
  };

  return (
    <TicketContext.Provider value={contextValue}>
      {children}
    </TicketContext.Provider>
  );
};
